import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import { AllocationModel, PositionType, Side, Unit } from '../../../../Generated/Commodity-Management-Api';
import { StoneXGrid } from '../../../StoneX';
import RollVolumeModal from './RollVolumeModal';
import UploadDeliveries from './UploadDeliveries';
import { FiscalMonth, monthSortKey } from '../../../../Helpers';
import { getCurrentForecastForMonth, getBaseVolumeForMonth } from './BudgetRoundHelpers';

interface LedgerProps {
    filteredAllocations: AllocationModel[];
    /** The 12 budget months of the fiscal year selected on the dashboard. */
    fiscalYearMonths: FiscalMonth[];
    selectedDisplayUnit?: Unit;
    onRollSuccess: () => void | Promise<void>;
}

const Ledger: React.FC<LedgerProps> = ({ filteredAllocations, fiscalYearMonths, selectedDisplayUnit, onRollSuccess }) => {
    const [ledgerChartSelection, setLedgerChartSelection] = useState<string>('total');
    const [rollModalOpen, setRollModalOpen] = useState(false);
    const [rollModalContext, setRollModalContext] = useState<{ year: number; month: number; excessVolume: number; allocation: AllocationModel; } | null>(null);

    // Compute ledgerData with useMemo
    const ledgerData = useMemo(() => {
        return filteredAllocations.map(alloc => {
            const tableRows = fiscalYearMonths.map(({ year, month }) => {
                const monthDate = dayjs(`${year}-${String(month).padStart(2, '0')}-01`);

                // Deliveries are already converted in filteredAllocations, so use quantity directly
                const deliveredVolume = (alloc.deliveries ?? [])
                    .filter(delivery => {
                        const deliveryDate = dayjs(delivery.documentDate);
                        return delivery.vendor === alloc.counterparty.customerCounterpartyCode &&
                            delivery.plant === alloc.location.customerLocationCode &&
                            deliveryDate.isSame(monthDate, 'month');
                    })
                    .reduce((sum, delivery) => {
                        // quantity is already converted to selectedDisplayUnit in filteredAllocations
                        return sum + (delivery.quantity ?? 0);
                    }, 0);

                const getVolumeByPositionType = (types: PositionType[]): number => {
                    return alloc.positions
                        .filter(p => {
                            const positionDate = dayjs(`${p.budgetDate.year}-${p.budgetDate.month}-01`);
                            return types.includes(p.positionType) && positionDate.isSame(monthDate, 'month');
                        })
                        .reduce((sum, p) => sum + (p.volume! * (p.side === Side.Buy ? 1 : -1)), 0); // p.volume is converted
                };

                // Each supplier carries an equal share of the location's budget.
                const allocationShare = filteredAllocations.length > 0 ? 1 / filteredAllocations.length : 0;

                const budgetVolume = getBaseVolumeForMonth(alloc.budgets, year, month) * allocationShare;

                // Resolve the forecast round per month rather than picking one
                // global latest entryDate - a round covering only part of the
                // year would otherwise zero out every month it doesn't touch.
                const forecastVolume = (getCurrentForecastForMonth(alloc.budgets, year, month)?.volume ?? 0) * allocationShare;

                const physicalVolume = getVolumeByPositionType([PositionType.Physical]);

                const sourceYear = year;
                const sourceMonth = month;

                const columnDefinitions = [
                    { field: "month", headerName: "Month" },
                    {
                        field: "budgetVolume",
                        headerName: "Budget Volume",
                        valueFormatter: ({ value }: { value: number | null | undefined }) => value != null ? value.toLocaleString(undefined, { maximumFractionDigits: 0 }) : ''
                    },
                    {
                        field: "forecastVolume",
                        headerName: "Forecast Volume",
                        valueFormatter: ({ value }: { value: number | null | undefined }) => value != null ? value.toLocaleString(undefined, { maximumFractionDigits: 0 }) : ''
                    },
                    {
                        field: "forecastPercent",
                        headerName: "% of Budget",
                        valueFormatter: ({ value }: { value: number | null | undefined }) =>
                            value != null ? (value * 100).toFixed(0) + '%' : '',
                        renderCell: ({ value }: { value: number | null | undefined }) => {
                            let color = 'inherit';
                            if (value != null) {
                                if (value * 100 < 100) color = 'gold';
                                else if (value * 100 === 100) color = 'green';
                                else if (value * 100 > 100) color = 'red';
                            }
                            return (
                                <span style={{ color }}>
                                    {value != null ? (value * 100).toFixed(0) + '%' : ''}
                                </span>
                            );
                        }
                    },
                    {
                        field: "physicalVolume",
                        headerName: "Physical Volume",
                        valueFormatter: ({ value }: { value: number | null | undefined }) => value != null ? value.toLocaleString(undefined, { maximumFractionDigits: 0 }) : ''
                    },
                    {
                        field: "physicalPercent",
                        headerName: "% of Forecast",
                        valueFormatter: ({ value }: { value: number | null | undefined }) =>
                            value != null ? (value * 100).toFixed(0) + '%' : '',
                        renderCell: ({ value }: { value: number | null | undefined }) => {
                            let color = 'inherit';
                            if (value != null) {
                                if (value * 100 < 100) color = 'gold';
                                else if (value * 100 === 100) color = 'green';
                                else if (value * 100 > 100) color = 'red';
                            }
                            return (
                                <span style={{ color }}>
                                    {value != null ? (value * 100).toFixed(0) + '%' : ''}
                                </span>
                            );
                        }
                    },
                    {
                        field: "deliveredVolume",
                        headerName: "Delivered Volume",
                        valueFormatter: ({ value }: { value: number | null | undefined }) => value != null ? value.toLocaleString(undefined, { maximumFractionDigits: 0 }) : ''
                    },
                    {
                        field: "deliveredPercent",
                        headerName: "% of Physical",
                        valueFormatter: ({ value }: { value: number | null | undefined }) =>
                            value != null ? (value * 100).toFixed(0) + '%' : '',
                        renderCell: ({ value }: { value: number | null | undefined }) => {
                            let color = 'inherit';
                            if (value != null) {
                                if (value * 100 < 100) color = 'gold';
                                else if (value * 100 === 100) color = 'green';
                                else if (value * 100 > 100) color = 'red';
                            }
                            return (
                                <span style={{ color }}>
                                    {value != null ? (value * 100).toFixed(0) + '%' : ''}
                                </span>
                            );
                        }
                    },
                    {
                        field: "rollAction",
                        headerName: "",
                        cellRenderer: (params: any) => {
                            const data = params?.data;
                            if (!data || data.deliveredVolume >= data.physicalVolume) return null;
                            return (
                                <IconButton
                                    size="small"
                                    title="Roll undelivered volume to next month"
                                    onClick={() => {
                                        setRollModalContext({
                                            year: data.sourceYear,
                                            month: data.sourceMonth,
                                            excessVolume: data.physicalVolume - data.deliveredVolume,
                                            allocation: data.allocation
                                        });
                                        setRollModalOpen(true);
                                    }}
                                >
                                    <SwapHorizIcon fontSize="small" />
                                </IconButton>
                            );
                        }
                    }
                ];
                return {
                    month: monthDate.format('MMM-YY'),
                    sortKey: monthSortKey({ year, month }),
                    sourceYear,
                    sourceMonth,
                    allocation: alloc,
                    budgetVolume,
                    forecastVolume,
                    deliveredVolume,
                    physicalVolume,
                    forecastPercent: budgetVolume ? (forecastVolume / budgetVolume) : 0,
                    physicalPercent: forecastVolume ? (physicalVolume / forecastVolume) : 0,
                    deliveredPercent: physicalVolume ? (deliveredVolume / physicalVolume) : 0,
                    columnDefinitions
                };
            });

            return {
                allocationName: alloc.counterparty.counterpartyName,
                rows: tableRows,
            };
        });
    }, [filteredAllocations, fiscalYearMonths]);

    // Aggregated monthly chart data
    const ledgerChartTotalData = useMemo(() => {
        const monthly: Record<string, { month: string; sortKey: number; budgetVolume: number; forecastVolume: number; physicalVolume: number; deliveredVolume: number; }> = {};
        for (const allocation of ledgerData) {
            for (const row of allocation.rows) {
                if (!monthly[row.month]) {
                    monthly[row.month] = {
                        month: row.month,
                        sortKey: row.sortKey,
                        budgetVolume: 0,
                        forecastVolume: 0,
                        physicalVolume: 0,
                        deliveredVolume: 0,
                    };
                }
                monthly[row.month].budgetVolume += row.budgetVolume ?? 0;
                monthly[row.month].forecastVolume += row.forecastVolume ?? 0;
                monthly[row.month].physicalVolume += row.physicalVolume ?? 0;
                monthly[row.month].deliveredVolume += row.deliveredVolume ?? 0;
            }
        }
        return Object.values(monthly).sort((a, b) => a.sortKey - b.sortKey);
    }, [ledgerData]);

    // Per-supplier monthly chart data: shows the same 4 volumes for a single supplier
    const ledgerChartDataBySupplier = useMemo(() => {
        const bySupplier: Record<string, { month: string; sortKey: number; budgetVolume: number; forecastVolume: number; physicalVolume: number; deliveredVolume: number; }[]> = {};
        for (const allocation of ledgerData) {
            bySupplier[allocation.allocationName] = allocation.rows
                .map(r => ({
                    month: r.month,
                    sortKey: r.sortKey,
                    budgetVolume: r.budgetVolume ?? 0,
                    forecastVolume: r.forecastVolume ?? 0,
                    physicalVolume: r.physicalVolume ?? 0,
                    deliveredVolume: r.deliveredVolume ?? 0,
                }))
                .sort((a, b) => a.sortKey - b.sortKey);
        }
        return bySupplier;
    }, [ledgerData]);

    const activeChartData = useMemo(() => {
        const base = ledgerChartSelection === 'total'
            ? ledgerChartTotalData
            : (ledgerChartDataBySupplier[ledgerChartSelection] ?? []);
        return base.map(r => ({
            ...r,
            deliveredPercent: r.physicalVolume > 0 ? r.deliveredVolume / r.physicalVolume : 0,
        }));
    }, [ledgerChartSelection, ledgerChartTotalData, ledgerChartDataBySupplier]);

    return (
        <>
            {ledgerData.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Monthly Volumes {selectedDisplayUnit ? `(${selectedDisplayUnit})` : ''}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="ledger-chart-scope-label">View</InputLabel>
                            <Select
                                labelId="ledger-chart-scope-label"
                                label="View"
                                value={ledgerChartSelection}
                                onChange={(e) => setLedgerChartSelection(e.target.value as string)}
                            >
                                <MenuItem value="total">Total</MenuItem>
                                {ledgerData.map(a => (
                                    <MenuItem key={a.allocationName} value={a.allocationName}>{a.allocationName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box
                        sx={{
                            width: '100%',
                            height: 360,
                            p: 2,
                            backgroundColor: 'white',
                            borderRadius: 4,
                            boxSizing: 'border-box',
                        }}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                                data={activeChartData}
                                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                <YAxis yAxisId="left" tickFormatter={(value: number) => value.toLocaleString()} />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    domain={[0, 1.25]}
                                    ticks={[0, 0.25, 0.5, 0.75, 1, 1.25]}
                                    tickFormatter={(value: number) => `${Math.round(value * 100)}%`}
                                />
                                <Tooltip
                                    formatter={(value, _name, item) => {
                                        if (item && (item as any).dataKey === 'deliveredPercent') {
                                            return typeof value === 'number'
                                                ? `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
                                                : String(value ?? '');
                                        }
                                        return typeof value === 'number'
                                            ? value.toLocaleString(undefined, { maximumFractionDigits: 0 })
                                            : String(value ?? '');
                                    }}
                                    itemSorter={(item) => {
                                        const order: Record<string, number> = {
                                            forecastVolume: 0,
                                            physicalVolume: 1,
                                            deliveredVolume: 2,
                                            deliveredPercent: 3,
                                        };
                                        return order[item.dataKey as string] ?? 99;
                                    }}
                                />
                                <Legend
                                    content={() => {
                                        const items = [
                                            { value: 'Forecast', color: '#5c6bc0', shape: 'square' as const },
                                            { value: 'Physical', color: '#9fa8da', shape: 'square' as const },
                                            { value: 'Delivered', color: '#c5cae9', shape: 'square' as const },
                                            { value: 'Delivered % of Physical', color: '#d32f2f', shape: 'line' as const },
                                        ];
                                        return (
                                            <ul style={{ display: 'flex', justifyContent: 'center', gap: 16, listStyle: 'none', padding: 0, margin: 0 }}>
                                                {items.map(item => (
                                                    <li key={item.value} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        <span style={{ display: 'inline-block', width: 12, height: item.shape === 'line' ? 2 : 12, backgroundColor: item.color }} />
                                                        <span style={{ fontSize: 12, color: '#666' }}>{item.value}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        );
                                    }}
                                />
                                <Bar yAxisId="left" dataKey="forecastVolume" name="Forecast" fill="#5c6bc0" />
                                <Bar yAxisId="left" dataKey="physicalVolume" name="Physical" fill="#9fa8da" />
                                <Bar yAxisId="left" dataKey="deliveredVolume" name="Delivered" fill="#c5cae9" />
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="deliveredPercent"
                                    name="Delivered % of Physical"
                                    stroke="#d32f2f"
                                    strokeWidth={2}
                                    dot={{ r: 3, fill: '#d32f2f' }}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>
            )}
            {ledgerData.filter(data => data.rows.length > 0).map(data => (
                <Box key={data.allocationName} sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>{data.allocationName}</Typography>
                    <StoneXGrid
                        columns={data.rows[0].columnDefinitions}
                        rows={data.rows}
                        chartButtonDisplayCondition={x => x.data !== undefined}
                        expandCollapseRowCondition={x => x.data !== undefined} />
                </Box>
            ))}

            <UploadDeliveries />

            {rollModalContext && (
                <RollVolumeModal
                    open={rollModalOpen}
                    onClose={() => setRollModalOpen(false)}
                    onSuccess={onRollSuccess}
                    sourceYear={rollModalContext.year}
                    sourceMonth={rollModalContext.month}
                    excessVolume={rollModalContext.excessVolume}
                    allocation={rollModalContext.allocation}
                    displayUnit={selectedDisplayUnit}
                />
            )}
        </>
    );
};

export default Ledger;
